import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { AdministratorAndRoles, Role } from '@mytypes';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { useTranslation } from 'react-i18next';
import { FONT_WEIGHTS, FONT_SIZES, PALETTE } from '~/assets/styles/base';
import { ADMINISTRATOR_TABLE_HEAD } from '~/constants';
import EditButton from '~/components/ui-elements/EditButton';
import RolesModal from '../RolesModal/index';
import AlertSuccess from '~/components/ui-parts/AlertSuccess';

const StyledTable = styled(Table)`
  min-width: 1260px;
`;

const StyledTableHead = styled(TableHead)`
  background-color: ${PALETTE.blue.main};
  border: 1px solid ${PALETTE.border.table};
  border-width: 2px 1px 2px 1px;
  th {
    font-size: ${FONT_SIZES.body3};
    font-weight: ${FONT_WEIGHTS.base};
    height: 36px;
    padding: 10px 12px;
    vertical-align: middle;
    white-space: nowrap;
  }
`;

const StyledTableBody = styled(TableBody)`
  border: 1px solid ${PALETTE.border.table};
  border-top: none;
  tr {
    background-color: ${PALETTE.white};
    &:nth-child(even) {
      background-color: ${PALETTE.blue.light};
    }
  }
  td {
    font-size: ${FONT_SIZES.body3};
    height: 44px;
    padding: 10px 12px;
    border-bottom: 1px solid ${PALETTE.border.table};
  }
`;

type Props = {
  administratorsAndRoles: AdministratorAndRoles[];
  roles: Role[];
};

type AdminUpdated = {
  created: string;
  email: string;
  id: number;
  modified: string;
  name: string;
  roles: number[];
};

const AdministratorAndRolesTable: React.FC<Props> = ({ administratorsAndRoles, roles }) => {
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const [adminUpdated, setAdminUpdated] = useState<AdminUpdated>({
    created: '',
    email: '',
    id: 0,
    modified: '',
    name: '',
    roles: [],
  });

  const [alert, setAlert] = useState(false);
  useEffect(() => {
    if (alert) {
      setTimeout(() => {
        setAlert(false);
      }, 3000);
    }
  }, [alert]);

  const handleOpenAlert = (isOpen: boolean) => setAlert(isOpen);
  const styleCell = {
    backgroundColor: '#E5EEF5',
    textAlign: 'center',
  };
  const styleActionColumn = {
    width: '105px',
  };
  return (
    <TableContainer>
      <AlertSuccess
        data-testid="AdministratorAndRolesTableAlert"
        head={t('administrators.alertSuccess.head')}
        body={t('administrators.alertSuccess.body')}
        open={alert}
        onClose={() => setAlert(false)}
      />
      <StyledTable>
        <StyledTableHead>
          <tr data-testid="AdministratorAndRolesTableHeader">
            {ADMINISTRATOR_TABLE_HEAD.map((elem) => (
              <TableCell sx={elem.id === 'action' ? styleCell : {}} key={elem.id}>
                {elem.text}
              </TableCell>
            ))}
          </tr>
        </StyledTableHead>
        <StyledTableBody>
          {administratorsAndRoles?.map((admin, i) => (
            <TableRow key={admin.id} data-testid={`AdministratorAndRolesTableRow-${i}`}>
              <TableCell component="td" scope="row">
                {admin.id}
              </TableCell>
              <TableCell component="td" scope="row">
                {admin.name}
              </TableCell>
              <TableCell component="td" scope="row">
                {admin.email}
              </TableCell>
              <TableCell component="td" scope="row">
                {admin.roles
                  .map((roleId) => roles.find((role) => role.id === roleId)?.name)
                  .join(', ')}
              </TableCell>
              <TableCell component="td" scope="row" style={styleActionColumn}>
                <EditButton
                  name={`updateRoles-${i}`}
                  sx={{ width: 148, marginLeft: 26 }}
                  handleClick={() => {
                    setOpenModal(true);
                    setAdminUpdated(admin);
                  }}
                >
                  <EditRoundedIcon />
                </EditButton>
              </TableCell>
            </TableRow>
          ))}
        </StyledTableBody>
      </StyledTable>
      <RolesModal
        open={openModal}
        handleClose={() => setOpenModal(false)}
        options={roles}
        rolesAdmin={adminUpdated}
        setAlert={handleOpenAlert}
      />
    </TableContainer>
  );
};

export default AdministratorAndRolesTable;
